import {announce} from '@github-ui/aria-live'
import {Banner, Link, LinkButton} from '@primer/react'
import {useCallback, useEffect, useState} from 'react'
import type {AgentQuotaSnapshot} from '../../types/agent-quota-snapshot'
import styles from './QuotaBanner.module.css'
import {MANAGE_BUDGET_URL, UPGRADE_PLAN_URL} from '@github-ui/copilot-chat/utils/copilot-chat-helpers'
import {
  getQuotaBannerDescription,
  getQuotaBannerState,
  isBlockingQuotaState,
  isHighSeverityApproachingLimit,
} from '../../utils/quota-banner-state'

type QuotaBannerProps = {
  agentQuotaSnapshot?: AgentQuotaSnapshot | null
}

// Pass an element so the ariaNotify polyfill can locate the correct live
// region root when the banner renders inside a dialog (the polyfill suppresses
// announcements from elements outside the active modal).
function useAnnounceMessage(message: string | null, assertive: boolean, element: HTMLElement | null) {
  useEffect(() => {
    if (message && element) {
      announce(message, {assertive, element})
    }
  }, [message, assertive, element])
}

export function QuotaBanner({agentQuotaSnapshot}: QuotaBannerProps) {
  const [containerEl, setContainerEl] = useState<HTMLDivElement | null>(null)
  const containerRef = useCallback((node: HTMLDivElement | null) => setContainerEl(node), [])

  const state = getQuotaBannerState(agentQuotaSnapshot ?? undefined)
  const description = getQuotaBannerDescription(state)

  // Announce the same copy the banner renders, so screen-reader users hear the
  // dynamic "used X%" value instead of a coarse static bucket. Blocked states
  // are assertive (the submit action is also disabled); the approaching-limit
  // warning is announced politely.
  useAnnounceMessage(description || null, isBlockingQuotaState(state), containerEl)

  // agentQuotaSnapshot isn't defined when the user has no agent access or quota info is unavailable
  if (!agentQuotaSnapshot) return null

  switch (state.kind) {
    case 'cfiBlocked': {
      return (
        <div ref={containerRef} className={styles.container} data-testid="quota-banner">
          <Banner
            variant="critical"
            title="Insufficient AI credits"
            hideTitle
            description={description}
            primaryAction={
              <LinkButton as="a" href={MANAGE_BUDGET_URL}>
                Enable additional usage
              </LinkButton>
            }
            secondaryAction={
              state.canUpgradePlan ? (
                <Link as="a" href={UPGRADE_PLAN_URL}>
                  Upgrade plan
                </Link>
              ) : undefined
            }
          />
        </div>
      )
    }
    case 'cfiBudgetHit': {
      return (
        <div ref={containerRef} className={styles.container} data-testid="quota-banner">
          <Banner
            variant="critical"
            title="Additional usage budget reached"
            hideTitle
            description={description}
            primaryAction={
              <LinkButton as="a" href={MANAGE_BUDGET_URL}>
                Increase budget
              </LinkButton>
            }
            secondaryAction={
              state.canUpgradePlan ? (
                <Link as="a" href={UPGRADE_PLAN_URL}>
                  Upgrade plan
                </Link>
              ) : undefined
            }
          />
        </div>
      )
    }
    case 'ceCbBlocked': {
      return (
        <div ref={containerRef} className={styles.container} data-testid="quota-banner">
          <Banner variant="critical" title="Insufficient AI credits" hideTitle description={description} />
        </div>
      )
    }
    case 'approachingLimit': {
      // CFI users can self-serve: surface budget management (and upgrade plan
      // when eligible). CB/CE users are managed by their org admin so we
      // render the warning without CTAs to match the rest of the spec.
      // When the user is consuming additional usage credits (overageMode),
      // swap "Manage budget" → "Increase budget" to match the CFI guide.
      const showBudgetCta = state.isIndividual
      const showUpgradePlan = state.isIndividual && state.canUpgradePlan

      const budgetAction = showBudgetCta ? (
        <LinkButton as="a" href={MANAGE_BUDGET_URL}>
          {state.overageMode ? 'Increase budget' : 'Manage budget'}
        </LinkButton>
      ) : undefined

      const upgradePlanAction = showUpgradePlan ? (
        <Link as="a" href={UPGRADE_PLAN_URL}>
          Upgrade plan
        </Link>
      ) : undefined

      return (
        <div ref={containerRef} className={styles.container} data-testid="quota-banner">
          <Banner
            variant={isHighSeverityApproachingLimit(state.percentUsed) ? 'warning' : 'info'}
            title="Usage limit warning"
            hideTitle
            description={description}
            primaryAction={budgetAction}
            secondaryAction={upgradePlanAction}
          />
        </div>
      )
    }
    case 'none':
    default:
      return null
  }
}
